import type { AudioManifest, AudioSegment, Paper, VoiceIntent } from "@/api/types";

export type ListenStage = "new" | "review";
export type ListenMode = "brief" | "detail" | "abstract" | "all";
export type ListenPurpose = ListenMode;
type SingleTrackPurpose = Exclude<ListenPurpose, "all">;

const READY_STATES = new Set(["ready", "success", "done", "cached"]);
const FAILED_STATES = new Set(["failed", "blocked", "error"]);
const VOICE_INTENTS = new Set<VoiceIntent>([
  "keep", "review", "deep_read", "later", "archive", "skip", "replay",
  "detail", "brief", "abstract", "undo", "pause", "none",
]);

export function audioStageForPaper(paper: Pick<Paper, "status">): ListenStage {
  return paper.status === "review" || paper.status === "deep_read" ? "review" : "new";
}

export function normalizeListenMode(value: unknown, stage: ListenStage): ListenMode {
  return value === "brief" || value === "detail" || value === "abstract" || value === "all"
    ? value
    : stage === "review" ? "detail" : "brief";
}

export function listenModeStorageKey(stage: ListenStage): string {
  return `dr_voice_content_${stage}`;
}

export function effectiveListenPurpose(stage: ListenStage, mode?: unknown): ListenPurpose {
  return normalizeListenMode(mode, stage);
}

export function normalizedTrackName(track: { kind?: string; track?: string; name?: string }): string {
  return String(track.kind ?? track.track ?? track.name ?? "").trim().toLowerCase();
}

function segmentIdentity(segment: AudioSegment): string {
  return segment.audio_uid ?? segment.url ?? `${segment.track}:${segment.part}:${segment.order}`;
}

function readySegments(segments: AudioSegment[]): AudioSegment[] {
  const seen = new Set<string>();
  return [...segments]
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
    .filter((segment) => {
      const key = segmentIdentity(segment);
      if (seen.has(key)) return false;
      seen.add(key);
      return READY_STATES.has((segment.state ?? "ready").toLowerCase());
    });
}

export function segmentsFrom(manifest: AudioManifest): AudioSegment[] {
  const seen = new Set<string>();
  const ordered = [
    ...(manifest.tracks ?? []).flatMap((track) => readySegments(track.segments ?? [])),
    ...readySegments(manifest.segments ?? []),
  ];
  return ordered.filter((segment) => {
    const key = segmentIdentity(segment);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function trackKindFor(stage: ListenStage, purpose: SingleTrackPurpose): string {
  if (purpose === "abstract") return "abstract";
  if (stage === "new") return purpose === "brief" ? "brief" : "summary_detail";
  return purpose === "brief" ? "review_brief" : "deep_summary";
}

export function audioSegmentsFor(
  manifest: AudioManifest,
  stage: ListenStage,
  purpose: ListenPurpose = effectiveListenPurpose(stage),
): AudioSegment[] {
  if (purpose === "all") {
    const detail = audioSegmentsFor(manifest, stage, "detail");
    const abstract = audioSegmentsFor(manifest, stage, "abstract");
    if ((manifest.tracks?.length ?? 0) > 0 && (!detail.length || !abstract.length)) return [];
    const seen = new Set<string>();
    return [...detail, ...abstract].filter((segment) => {
      const key = segmentIdentity(segment);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  const wanted = trackKindFor(stage, purpose);
  const selected = (manifest.tracks ?? [])
    .filter((track) => normalizedTrackName(track) === wanted)
    .flatMap((track) => readySegments(track.segments ?? []));
  if (selected.length || (manifest.tracks?.length ?? 0) > 0) {
    const seen = new Set<string>();
    return selected.filter((segment) => {
      const key = segmentIdentity(segment);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }
  return purpose === "abstract" ? [] : readySegments(manifest.segments ?? []);
}

export function prefetchSegmentsFor(
  manifest: AudioManifest,
  stage: ListenStage,
  mode?: ListenMode,
): AudioSegment[] {
  return audioSegmentsFor(manifest, stage, effectiveListenPurpose(stage, mode));
}

export function manifestFailures(
  manifest: AudioManifest,
  stage: ListenStage,
  purpose: ListenPurpose = effectiveListenPurpose(stage),
): {
  audio: boolean;
  summary: boolean;
  message: string;
} {
  if (purpose === "all") {
    const detail = manifestFailures(manifest, stage, "detail");
    const abstract = manifestFailures(manifest, stage, "abstract");
    return {
      audio: detail.audio || abstract.audio,
      summary: detail.summary,
      message: detail.message || abstract.message,
    };
  }
  const summary = purpose !== "abstract"
    && FAILED_STATES.has((manifest.summary_state ?? "").toLowerCase());
  const targetKind = trackKindFor(stage, purpose);
  const selectedTrack = (manifest.tracks ?? []).find((track) =>
    normalizedTrackName(track) === targetKind,
  );
  const failedTrack = selectedTrack && FAILED_STATES.has((selectedTrack.state ?? "").toLowerCase())
    ? selectedTrack
    : undefined;
  const failedSegment = (selectedTrack?.segments ?? []).find((segment) =>
    FAILED_STATES.has((segment.state ?? "").toLowerCase()),
  );
  const topLevelFailure = (manifest.tracks?.length ?? 0) === 0
    && FAILED_STATES.has((manifest.state ?? "").toLowerCase());
  const audio = topLevelFailure || Boolean(failedTrack) || Boolean(failedSegment);
  return {
    audio,
    summary,
    message: String(failedSegment?.error ?? failedTrack?.error ?? manifest.error ?? ""),
  };
}

export type DeterministicListenIntent = SingleTrackPurpose | "ambiguous" | null;

export function deterministicListenIntent(text: string): DeterministicListenIntent {
  const value = text.trim().toLowerCase();
  if (!value) return null;
  const hits = new Set<SingleTrackPurpose>();
  if (/(详细|完整)(总结|播报|内容)?|读完整/.test(value)) hits.add("detail");
  if (/(精简|简短|简要)(总结|播报|内容)?|读精简/.test(value)) hits.add("brief");
  if (/(原文)?摘要|读原文/.test(value)) hits.add("abstract");
  const hasDecision = /(归档|保留|稍后|复筛|确认精读|去精读|跳过|下一篇|撤销|取消|暂停|停止)/.test(value);
  if (hits.size > 1 || (hits.size === 1 && hasDecision)) return "ambiguous";
  return hits.values().next().value ?? null;
}

export interface ResolvedVoiceIntent {
  intent: VoiceIntent;
  source: "local" | "server" | "ambiguous";
}

export async function resolveVoiceIntent(
  text: string,
  fallback: (text: string) => Promise<{ intent: VoiceIntent }>,
): Promise<ResolvedVoiceIntent> {
  const local = deterministicListenIntent(text);
  if (local === "ambiguous") return { intent: "none", source: "ambiguous" };
  if (local) return { intent: local, source: "local" };
  const result = await fallback(text);
  return {
    intent: VOICE_INTENTS.has(result.intent) ? result.intent : "none",
    source: "server",
  };
}

export function isListenPurpose(intent: VoiceIntent): intent is SingleTrackPurpose {
  return intent === "brief" || intent === "detail" || intent === "abstract";
}
